import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { api, mediaUrl, formatMoney } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Users, TrendingDown, TrendingUp, ExternalLink, Truck, MapPin } from 'lucide-react';
import { SafeImage } from '@/components/marketplace/SafeImage';
import { RatingStars } from '@/components/marketplace/RatingStars';
import { VerifiedBadge } from '@/components/common/VerifiedBadge';

const LS_KEY = 'okltvendo:destination_zip';
const LS_CARRIER = 'okltvendo:preferred_carrier';

const CARRIERS = [
    { value: 'dhl', label: 'DHL' },
    { value: 'fedex', label: 'FedEx' },
    { value: 'estafeta', label: 'Estafeta' },
    { value: 'redpack', label: 'Redpack' },
];

export const OtherSellersOfProduct = ({ ad }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [zip, setZip] = useState(() => (typeof window !== 'undefined' && localStorage.getItem(LS_KEY)) || '');
    const [zipInput, setZipInput] = useState(zip);
    const [realShipping, setRealShipping] = useState(() => (typeof window !== 'undefined' && localStorage.getItem('okltvendo:real_shipping') === '1'));
    const [carrier, setCarrier] = useState(() => (typeof window !== 'undefined' && localStorage.getItem(LS_CARRIER)) || 'dhl');

    const load = useCallback(async () => {
        if (!ad?.id) return;
        setLoading(true);
        try {
            const params = {};
            if (zip && /^\d{4,5}$/.test(zip)) params.destination_zip = zip;
            if (realShipping) { params.real_shipping = true; params.carrier = carrier; }
            const { data } = await api.get(`/ads/${ad.id}/other-sellers`, { params });
            setData(data);
        } catch { setData(null); } finally { setLoading(false); }
    }, [ad?.id, zip, realShipping, carrier]);

    useEffect(() => { load(); }, [load]);

    const applyZip = () => {
        const v = (zipInput || '').replace(/\D/g, '').slice(0, 5);
        if (v.length < 4) return;
        localStorage.setItem(LS_KEY, v);
        setZip(v);
    };

    if (loading || !data || !data.items || data.items.length === 0) return null;

    const refPrice = Number(data.reference_price) || 0;
    const refShip = data.reference_shipping;
    const refTotal = refPrice + (refShip?.amount || 0);

    return (
        <section className="mt-10" data-testid="other-sellers-section">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
                <Users className="h-5 w-5 text-[hsl(var(--primary))]" />
                <h2 className="text-xl font-semibold tracking-tight">Otros vendedores lo tienen</h2>
                <Badge variant="outline" className="ml-1">{data.items.length}</Badge>
                <div className="ml-auto flex items-center gap-2 bg-slate-50 border rounded-full px-2 py-1 flex-wrap">
                    <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">Enviar a CP</span>
                    <Input
                        value={zipInput}
                        onChange={(e) => setZipInput(e.target.value.replace(/\D/g, '').slice(0, 5))}
                        placeholder="00000"
                        className="h-6 w-16 text-xs border-0 focus-visible:ring-0 bg-transparent"
                        data-testid="destination-zip-input"
                    />
                    <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={applyZip} data-testid="apply-zip">Aplicar</Button>
                    <span className="w-px h-4 bg-slate-300" />
                    <label className="flex items-center gap-1 text-[11px] cursor-pointer select-none" title="Consulta cotización real con OkEnvíos (más lento, más preciso)">
                        <input
                            type="checkbox"
                            checked={realShipping}
                            onChange={(e) => { setRealShipping(e.target.checked); localStorage.setItem('okltvendo:real_shipping', e.target.checked ? '1' : '0'); }}
                            className="h-3 w-3 accent-[hsl(var(--primary))]"
                            data-testid="toggle-real-shipping"
                        />
                        <Truck className="h-3 w-3" />Real
                    </label>
                    {realShipping && (
                        <>
                            <span className="w-px h-4 bg-slate-300" />
                            <select
                                className="h-6 text-[11px] bg-transparent border-0 focus:outline-none focus:ring-0 cursor-pointer"
                                value={carrier}
                                onChange={(e) => { setCarrier(e.target.value); localStorage.setItem(LS_CARRIER, e.target.value); }}
                                data-testid="carrier-select"
                            >
                                {CARRIERS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                            </select>
                        </>
                    )}
                </div>
            </div>
            <p className="text-xs text-muted-foreground mb-4">
                Compara precio, calificaciones y envío estimado. {zip ? `Envío estimado a CP ${zip}.` : 'Ingresa tu código postal para ver envío estimado.'}
            </p>

            {refShip && (
                <div className="mb-3 text-xs bg-amber-50 border border-amber-200 rounded p-2 flex items-center gap-2" data-testid="ref-total">
                    <Truck className="h-3.5 w-3.5 text-amber-700" />
                    <span>Este anuncio: <strong>{formatMoney(refPrice)}</strong> + envío <strong>{formatMoney(refShip.amount)}</strong> = total <strong>{formatMoney(refTotal)}</strong></span>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {data.items.map(o => {
                    const price = Number(o.price) || 0;
                    const ship = o.shipping_estimate;
                    const total = price + (ship?.amount || 0);
                    const diffTotal = refTotal > 0 && ship ? ((total - refTotal) / refTotal) * 100 : 0;
                    const cheaper = diffTotal < 0;
                    return (
                        <Card key={o.id} className="hover:shadow-md transition-shadow" data-testid={`other-seller-${o.id}`}>
                            <CardContent className="p-3">
                                <Link to={`/ad/${o.id}`} className="flex gap-3">
                                    <div className="w-20 h-20 rounded overflow-hidden bg-slate-100 shrink-0">
                                        <SafeImage src={mediaUrl(o.media?.[0]?.url)} alt={o.title} className="w-full h-full object-cover" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium line-clamp-2">{o.title}</div>
                                        <div className="flex items-baseline gap-2 mt-1">
                                            <div className="text-lg font-bold">{formatMoney(price)}</div>
                                        </div>
                                        {ship && (
                                            <div className="mt-1 text-[11px] flex items-center gap-1 text-muted-foreground">
                                                <Truck className="h-3 w-3" />
                                                Envío {ship.source === 'real' ? '' : 'est. '}{formatMoney(ship.amount)}
                                                {ship.source === 'real' && <span className="text-emerald-700 font-semibold">· real</span>}
                                            </div>
                                        )}
                                        {ship && (
                                            <div className="mt-1 flex items-center gap-2">
                                                <span className="text-xs font-semibold">Total {formatMoney(total)}</span>
                                                {refTotal > 0 && diffTotal !== 0 && (
                                                    <span className={`text-xs font-medium flex items-center gap-0.5 ${cheaper ? 'text-emerald-700' : 'text-amber-700'}`}>
                                                        {cheaper ? <TrendingDown className="h-3 w-3" /> : <TrendingUp className="h-3 w-3" />}
                                                        {Math.abs(diffTotal).toFixed(0)}%
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </Link>
                                {o.seller && (
                                    <div className="mt-2 pt-2 border-t flex items-center justify-between text-xs">
                                        <Link to={`/u/${o.seller.nickname}`} className="flex items-center gap-1 hover:underline min-w-0">
                                            <span className="font-medium truncate">@{o.seller.nickname}</span>
                                            {o.seller.verified && <VerifiedBadge verified={true} className="!h-3 !w-3" />}
                                        </Link>
                                        <div className="flex items-center gap-1">
                                            <RatingStars value={o.seller.rating_avg || 0} size="sm" />
                                            <span className="text-muted-foreground">({o.seller.rating_count || 0})</span>
                                        </div>
                                    </div>
                                )}
                                <Link to={`/ad/${o.id}`} className="mt-2 text-xs text-primary hover:underline inline-flex items-center gap-1" data-testid={`view-other-${o.id}`}>
                                    Ver oferta <ExternalLink className="h-3 w-3" />
                                </Link>
                            </CardContent>
                        </Card>
                    );
                })}
            </div>
            {zip && (
                <p className="text-[10px] text-muted-foreground mt-3">
                    {realShipping ? '* Cotización real via OkEnvíos (DHL más barato).' : '* Costo de envío estimado con heurística.'} {' '}El valor final se calcula al generar la guía real.
                </p>
            )}
        </section>
    );
};

export default OtherSellersOfProduct;
